/**
 * @fileoverview StatusBadgeCell — the ONE tone pill (Q-MI-25)
 * @package @puffin-core/page-shell
 *
 * Renders `.badge` with its tone on `data-tone` — the closed set success · warning · danger ·
 * info · neutral, every tone SOFT (shared-styles `badges.css`). This is the only primitive that
 * emits the tone pill: a surface hands it MEANING — a frozen `status → tone` map declared beside
 * its status vocabulary, or a tone it computed — and the class is this component's business.
 * CSS-003: dynamic state is an attribute, never a class modifier, so no status or tone ever
 * becomes a class name (`web/no-constructed-status-class`).
 *
 * The tone resolves through `resolveBadgeTone` (@puffin-core/status-taxonomy), which REFUSES a
 * value outside the closed set: a tone the CSS does not define would render the bare base with no
 * error anywhere, so it throws here instead.
 *
 * Domain statuses with an OPEN vocabulary keep `.status-pill[data-status]`; the publish lifecycle
 * keeps `<LifecyclePill>`. Count, outline, source and role badges keep their own classes.
 *
 * Sibling list-page rollout Phase 0 (2026-04-24); tone API Q-MI-25 (2026-09-25).
 */

import PropTypes from 'prop-types';
import { BADGE_TONE_VALUES, resolveBadgeTone } from '@puffin-core/status-taxonomy';

/**
 * @param {Object} props
 * @param {string} [props.status] - The raw domain status; drives the map lookup and `data-status`.
 * @param {Object<string,string>} [props.statusTones] - Frozen status → tone map.
 * @param {string} [props.defaultTone='neutral'] - Tone for a status the map does not list.
 * @param {string} [props.tone] - An explicit, caller-computed tone; wins over the map.
 * @param {Object<string,string>} [props.statusLabels] - Map status → display label.
 * @param {React.ReactNode} [props.label] - An explicit label; wins over `statusLabels`.
 * @param {boolean} [props.dot=false] - Render the leading status dot.
 * @param {string} [props.className] - Layout/size utilities appended after `badge` (never a tone).
 * @param {string} [props.title]
 * @param {string} [props.role] - e.g. `status` for a badge that reports a live state.
 * @param {string} [props.ariaLabel] - Accessible name; must keep the visible label in it (WCAG 2.5.3).
 * @param {'polite'|'assertive'|'off'} [props.ariaLive] - For a badge whose text updates in place
 *   (a countdown): the live-region politeness, stated explicitly.
 * @param {string} [props.fallback='—'] - Rendered when there is neither a status nor a label.
 * @param {string} [props.testId] - `data-testid` on the rendered span (badge and fallback).
 * @returns {React.JSX.Element}
 */
export function StatusBadgeCell({
  status,
  statusTones,
  defaultTone,
  tone,
  statusLabels,
  label,
  dot = false,
  className,
  title,
  role,
  ariaLabel,
  ariaLive,
  fallback = '—',
  testId,
}) {
  const hasStatus = typeof status === 'string' && status.length > 0;
  const text = label ?? (hasStatus ? (statusLabels && statusLabels[status]) || status : null);
  if (text === null || text === undefined) return <span data-testid={testId}>{fallback}</span>;
  const resolved = resolveBadgeTone({
    status: hasStatus ? status : undefined,
    tone,
    statusTones,
    ...(defaultTone === undefined ? {} : { defaultTone }),
  });
  // `data-status` carries the RAW status beside the resolved tone (CSS-003's idiom). A tone is
  // shared by several statuses; the status is not — so the attribute is what lets a suite prove
  // "the untranslated status drove this chip" (ECO-QUALITY-2026-09-07 Q-10).
  return (
    <span
      className={className ? `badge ${className}` : 'badge'}
      data-tone={resolved}
      data-status={hasStatus ? status : undefined}
      title={title}
      role={role}
      aria-label={ariaLabel}
      aria-live={ariaLive}
      data-testid={testId}
    >
      {dot ? <span className="badge-dot" aria-hidden="true" /> : null}
      {text}
    </span>
  );
}

StatusBadgeCell.propTypes = {
  status: PropTypes.string,
  statusTones: PropTypes.objectOf(PropTypes.oneOf(BADGE_TONE_VALUES)),
  defaultTone: PropTypes.oneOf(BADGE_TONE_VALUES),
  tone: PropTypes.oneOf(BADGE_TONE_VALUES),
  statusLabels: PropTypes.object,
  label: PropTypes.node,
  dot: PropTypes.bool,
  className: PropTypes.string,
  title: PropTypes.string,
  role: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaLive: PropTypes.oneOf(['polite', 'assertive', 'off']),
  fallback: PropTypes.string,
  testId: PropTypes.string,
};

export default StatusBadgeCell;
